import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import Input from '../components/Input';
import Button from '../components/Button';
import { ProductGridSkeleton } from '../components/Skeleton';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';

const ProductList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, categories, loading, fetchProducts } = useProducts();
  const { addItem } = useCart();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Convertir slug de URL a nombre de categoría
  useEffect(() => {
    const categorySlug = searchParams.get('categoria');
    if (categorySlug && categories.length > 0) {
      const category = categories.find(c => c.slug === categorySlug);
      setSelectedCategory(category ? category.name : '');
    } else if (!categorySlug) {
      setSelectedCategory('');
    }
  }, [searchParams, categories]);

  useEffect(() => {
    fetchProducts({
      category: selectedCategory || undefined,
      search: searchQuery || undefined,
    });
  }, [fetchProducts, selectedCategory, searchQuery]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category) {
      // Usar slug para URL limpia
      const categoryObj = categories.find(c => c.name === category);
      const slug = categoryObj ? categoryObj.slug : category.toLowerCase().replace(/\s+/g, '-');
      setSearchParams({ categoria: slug });
    } else {
      setSearchParams({});
    }
  };

  const handleAddToCart = (product) => {
    addItem(product, 1);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || 
      product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background pt-20 pb-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-display font-bold text-[#1B263B] mb-2">
            Nuestros Productos
          </h1>
          <p className="text-[#7A756F]">
            Encuentra el balón de gas perfecto para tu hogar
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A9590]" size={20} />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-4 bg-white border-2 border-[#EFEDEA] rounded-xl font-body text-[#1B263B] placeholder:text-[#9A9590] focus:border-primary focus:outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9590] hover:text-[#1B263B]"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            <Button
              variant={showFilters ? 'primary' : 'outline'}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className="flex-shrink-0"
            >
              <SlidersHorizontal size={20} />
            </Button>
          </div>

          {/* Category Filters */}
          {showFilters && (
            <div className="bg-white p-4 rounded-xl shadow-md animate-fadeIn">
              <p className="font-semibold text-[#1B263B] mb-3">Categorías</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCategoryChange('')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !selectedCategory
                      ? 'bg-primary text-white'
                      : 'bg-[#F8F7F5] text-[#7A756F] hover:bg-[#EFEDEA]'
                  }`}
                >
                  Todos
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.name)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category.name
                        ? 'bg-primary text-white'
                        : 'bg-[#F8F7F5] text-[#7A756F] hover:bg-[#EFEDEA]'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick category pills (always visible) */}
          {!showFilters && categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => handleCategoryChange('')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !selectedCategory
                    ? 'bg-primary text-white'
                    : 'bg-white text-[#7A756F] border border-[#EFEDEA] hover:border-primary'
                }`}
              >
                Todos
              </button>
              {categories.slice(0, 5).map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.name)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.name
                      ? 'bg-primary text-white'
                      : 'bg-white text-[#7A756F] border border-[#EFEDEA] hover:border-primary'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-[#7A756F] mb-4">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'producto' : 'productos'}
            {selectedCategory && ' en esta categoría'}
          </p>
        )}

        {/* Products Grid */}
        {loading ? (
          <ProductGridSkeleton count={8} />
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <ProductCard
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#F8F7F5] flex items-center justify-center">
              <Search size={32} className="text-[#BCB8B1]" />
            </div>
            <h3 className="font-display font-bold text-xl text-[#1B263B] mb-2">
              No se encontraron productos
            </h3>
            <p className="text-[#7A756F] max-w-md mx-auto">
              Intenta con otros términos de búsqueda o selecciona una categoría diferente.
            </p>
            {(searchQuery || selectedCategory) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  handleCategoryChange('');
                }}
                className="mt-4"
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
